import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import LoginImage from '@/assets/images/LoginImage.svg';
import LoginBg1 from '@/assets/images/login/login-bg-1.svg';
import LoginBg2 from '@/assets/images/login/login-bg-2.svg';
import LoginBg3 from '@/assets/images/login/login-bg-3.svg';
import LoginBg4 from '@/assets/images/login/login-bg-4.svg';

import { useAuth } from '@/contexts/auth';
import { useAppErrorToast } from '@/hooks/use-app-error-toast';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SiteFooter } from '@/components/SiteFooter';

const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(1, 'Senha é obrigatória')
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function Login() {
  const { login, loginLoading } = useAuth();
  const { showErrorToast } = useAppErrorToast();
  const [isMobile, setIsMobile] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onChange'
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const onSubmit = async (data: LoginFormData) => {
    try {
      await login(data);
    } catch (error: unknown) {
      showErrorToast(error, {
        title: 'Não foi possível fazer login',
        fallbackMessage: 'Verifique seu e-mail e senha e tente novamente.',
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
    <div className="bg-highlight flex-1">
      <div className="min-h-[100dvh] flex flex-col items-center justify-center relative container">
        <img
          src={LoginBg1}
          alt=""
          className="fixed bottom-0 right-0 pointer-events-none z-0 hidden md:block md:w-64 lg:w-80 xl:w-96 2xl:w-auto"
        />
        <img
          src={LoginBg2}
          alt=""
          className="fixed top-0 right-0 pointer-events-none z-0 hidden md:block md:w-32 lg:w-40 xl:w-48 2xl:w-56"
        />
        <img
          src={LoginBg3}
          alt=""
          className="fixed md:left-[40px] lg:left-[60px] xl:left-[80px] top-0 pointer-events-none z-0 md:w-[160px] md:h-[160px] lg:w-[220px] lg:h-[220px] xl:w-[280px] xl:h-[280px] rotate-[230deg] hidden md:block"
        />
        <img
          src={LoginBg3}
          alt=""
          className="fixed md:left-[-15px] lg:left-[-20px] xl:left-[-25px] md:top-[280px] lg:top-[320px] xl:top-[360px] pointer-events-none z-0 md:w-[80px] md:h-[80px] lg:w-[110px] lg:h-[110px] xl:w-[140px] xl:h-[140px] hidden md:block"
        />
        <img
          src={LoginBg3}
          alt=""
          className="fixed md:left-[80px] lg:left-[120px] xl:left-[160px] md:top-[420px] lg:top-[480px] xl:top-[540px] pointer-events-none z-0 md:w-[40px] md:h-[40px] lg:w-[52px] lg:h-[52px] xl:w-[64px] xl:h-[64px] hidden md:block"
        />
        <img
          src={LoginBg4}
          alt=""
          className="fixed left-0 bottom-0 pointer-events-none z-0 hidden md:block md:w-64 lg:w-80 xl:w-96 2xl:w-auto"
        />
        <img
          src={LoginBg3}
          alt=""
          className="fixed top-0 right-[-60px] pointer-events-none z-0 w-[120px] h-[120px] md:hidden"
        />
        <img
          src={LoginBg3}
          alt=""
          className="fixed top-[50px] left-[-20px] pointer-events-none z-0 w-[45px] h-[45px] md:hidden"
        />
        <img
          src={LoginBg3}
          alt=""
          className="fixed top-[250px] left-[-10px] pointer-events-none z-0 w-[45px] h-[45px] md:hidden"
        />
        <img
          src={LoginBg3}
          alt=""
          className="fixed top-[350px] left-[-5px] pointer-events-none z-0 w-[100px] h-[100px] md:hidden"
        />
        <img
          src={LoginBg3}
          alt=""
          className="fixed bottom-[300px] right-[10px] pointer-events-none z-0 w-[25px] h-[25px] md:hidden"
        />
        <img
          src={LoginBg3}
          alt=""
          className="fixed bottom-[200px] right-[30px] pointer-events-none z-0 w-[75px] h-[75px] md:hidden"
        />
        <img
          src={LoginBg3}
          alt=""
          className="fixed bottom-[-20px] right-[-20px] pointer-events-none z-0 w-[120px] h-[120px] md:hidden"
        />
        <div className="flex flex-col items-center mb-6 relative z-10 md:hidden md:mb-0 md:static md:z-0">
          <img src={LoginImage} alt={isMobile ? 'Logo Culture Monitor' : ''} className="w-44" />
        </div>
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-8 w-full max-w-96 flex flex-col gap-6 relative z-10">
          <div>
            <div className="mb-6">
              <h1 className="mb-2 font-sans font-semibold text-xl leading-none text-center">
                Faça login na sua conta
              </h1>
              <p className="font-sans text-[#737373] font-normal text-sm leading-none text-center">
                Digite seu e-mail e senha para acessar o sistema
              </p>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="email">
                  E-mail
                </label>
                <Input
                  id="email"
                  type="email"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Digite seu email"
                  error={errors.email?.message}
                  {...register('email')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" htmlFor="password">
                  Senha
                </label>
                <Input
                  id="password"
                  type="password"
                  error={errors.password?.message}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Digite sua senha"
                  {...register('password')}
                />
                <div className="flex justify-end">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-[#737373] underline"
                  >
                    Esqueci minha senha
                  </Link>
                </div>
              </div>
              <Button
                type="submit"
                disabled={!isValid || loginLoading}
                className="w-full bg-black text-white font-medium py-3 px-4 rounded-lg hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mt-6"
              >
                {loginLoading ? 'Entrando...' : 'Continuar'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
    <SiteFooter />
    </div>
  );
}
